import type { Customer } from "@graphql/generated/components";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { Consents } from "@lib/constants/contentful/consent";
import { useApi } from "@lib/helpers/useApi";
import { useStore } from "@store/index";
import { signIn } from "next-auth/react";
import { useRouter } from "next/router";

interface CreateAccountResponse {
    status: number;
    customer?: Customer;
}

const useCreateAccount = () => {
    const { basePath, locale } = useRouter();
    const setMasterProducts = useStore((state) => state.setMasterProducts);
    const setWishlistMasterProducts = useStore((state) => state.setWishlistMasterProducts);
    const setHasFreshData = useStore((state) => state.setHasFreshData);
    const setCustomer = useStore((state) => state.setCustomer);

    const handleCreateAccountMutation = useApi.useMutate<any, CreateAccountResponse>(
        `${basePath}/api/auth/sign-up`
    );

    async function createAccount(values): Promise<CreateAccountResponse> {
        const result = await handleCreateAccountMutation
            .mutateAsync({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                locale,
                password: values.password,
                newsletterKey: Consents.newsletter,
                phoneticFirstName: values.phoneticFirstName,
                phoneticLastName: values.phoneticLastName,
                dateOfBirth: values.dateOfBirth,
                privacyPolicyKey: Consents.privacyPolicy,
                termsAndConditionsKey: Consents.termsAndConditions,
            })
            .catch((error) => {
                return error;
            });

        if (result.error) {
            return {
                status: result.status,
            };
        }

        const { error } = await signIn("authenticated", {
            redirect: false,
            email: values.email,
            password: values.password,
        });

        if (error) {
            return null;
        }

        setMasterProducts(null);
        setWishlistMasterProducts(null);
        setHasFreshData(false);
        setCustomer(result);

        AnalyticsClient.accountCreate();
        return { status: 200, customer: result };
    }

    return { createAccount: createAccount };
};

export default useCreateAccount;
